import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Printer, X, Wallet, CreditCard, Ban, MapPin, Store, Phone, Clock, HandCoins, UserX, Smartphone } from 'lucide-react';
import { fmtMoney, fmtTimeAgo } from '@/lib/realtime';
import { ORDER_STATUS_COLORS, getContextualStatusLabel } from '@/lib/orderStatus';
import { DeliveryDispatchPanel } from './DeliveryDispatchPanel';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

// Same label table used by the printed receipt/kitchen ticket so the cashier
// sees identical wording on screen and on paper. Keep in sync with
// `OrdersQueue.jsx :: PAYMENT_METHOD_LABELS`.
const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  card: 'Card',
  mtn_money: 'Mobile Money (MTN)',
  orange_money: 'Mobile Money (Orange)',
  mobile_money: 'Mobile Money',
  cod: 'Pay on Delivery (COD)',
};
function paymentMethodLabel(order) {
  let method = (order.payment_method || 'cash').toLowerCase();
  if (order.order_type === 'delivery' && method === 'cash' && (order.payment_status || 'unpaid') !== 'paid') {
    method = 'cod';
  }
  return PAYMENT_METHOD_LABELS[method] || method.toUpperCase();
}

const ORDER_TYPE_LABEL = {
  dine_in: 'Dine-in',
  pickup: 'Pickup',
  delivery: 'Delivery',
};

export function OrderDetailDialog({
  order,
  open,
  onOpenChange,
  onMarkCashPaid,
  onMarkCardPaid,
  onMarkMobileMoneyPaid,
  onCancel,
  onMarkNoShow,
  onPrintReceipt,
  onDriverRemit,
  busy = false,
}) {
  if (!order) return null;
  const isPaid = order.payment_status === 'paid';
  // POS no longer advances kitchen status — the cashier card and detail dialog
  // strictly handle payments + cancellations + receipts. Kitchen progression
  // is owned by KDS.
  const canCancel = !['completed', 'delivered', 'cancelled'].includes(order.status);
  const canNoShow = (
    !['completed', 'delivered', 'cancelled'].includes(order.status)
    && (order.order_type === 'pickup' || order.order_type === 'delivery')
  );
  const driverHoldingCash = order.driver_cash_held && !order.driver_cash_remitted;
  const driverRemitted = order.driver_cash_remitted;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="order-detail-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            Order #{order.order_number}
            <Badge className={`border ${ORDER_STATUS_COLORS[order.status] || ''}`}>
              {getContextualStatusLabel(order.status, order.order_type, order.table_number)}
            </Badge>
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTimeAgo(order.created_at)}</span>
            <span>· {ORDER_TYPE_LABEL[order.order_type] || order.order_type}</span>
            <span>· {isPaid ? 'Paid' : 'Unpaid'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Customer / channel block */}
          <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Customer</p>
            <p className="font-semibold">{order.customer_name || 'Guest'}</p>
            {order.customer_phone && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Phone className="h-3 w-3" /> {order.customer_phone}
              </p>
            )}
            <Separator className="my-2" />
            {order.order_type === 'dine_in' && (
              <p className="inline-flex items-center gap-1 text-sm">
                <Store className="h-4 w-4 text-primary" />
                <strong>Table {order.table_number ?? '—'}</strong>
              </p>
            )}
            {order.order_type === 'pickup' && (
              <p className="inline-flex items-center gap-1 text-sm">
                <Store className="h-4 w-4 text-primary" />
                <strong>Counter pickup</strong>
              </p>
            )}
            {order.order_type === 'delivery' && order.delivery_address && (
              <div className="text-sm">
                <p className="inline-flex items-center gap-1 font-semibold">
                  <MapPin className="h-4 w-4 text-primary" /> Delivery address
                </p>
                <p className="mt-1 leading-tight text-xs text-muted-foreground">
                  {[order.delivery_address.line1, order.delivery_address.line2,
                    order.delivery_address.city, order.delivery_address.postal_code]
                    .filter(Boolean).join(', ')}
                </p>
                {order.delivery_verification_code && (
                  <p className="mt-1 text-xs">
                    Driver code: <span className="rounded bg-background px-1.5 py-0.5 font-mono text-sm tracking-widest">{order.delivery_verification_code}</span>
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Totals block */}
          <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Totals</p>
            <div className="space-y-1 tabular">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(order.subtotal, order.currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{fmtMoney(order.tax, order.currency)}</span></div>
              {order.delivery_fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{fmtMoney(order.delivery_fee, order.currency)}</span></div>}
              {order.tip > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tip</span><span>{fmtMoney(order.tip, order.currency)}</span></div>}
              <Separator className="my-1" />
              <div className="flex justify-between text-base font-semibold"><span>Total</span><span>{fmtMoney(order.total, order.currency)}</span></div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Payment</span>
                <Badge variant={isPaid ? 'default' : 'secondary'} className={isPaid ? 'bg-emerald-600 text-white hover:bg-emerald-600' : ''} data-testid="detail-payment-badge">
                  {(isPaid ? 'PAID' : (order.payment_status || 'unpaid').toUpperCase())} &middot; {paymentMethodLabel(order)}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery dispatch panel — only for active delivery orders. */}
        {order.order_type === 'delivery' && !['delivered', 'cancelled', 'completed'].includes(order.status) && (
          <DeliveryDispatchPanel order={order} />
        )}

        {/* Driver cash settlement banner — only for delivery orders where the
            driver has collected cash from the customer. */}
        {(driverHoldingCash || driverRemitted) && (
          <div
            data-testid="detail-driver-cash-section"
            className={`rounded-lg border p-3 text-sm ${driverHoldingCash ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}
          >
            <p className={`flex items-center gap-1.5 font-semibold ${driverHoldingCash ? 'text-amber-900' : 'text-emerald-900'}`}>
              <HandCoins className="h-4 w-4" />
              {driverHoldingCash
                ? `Driver is holding ${fmtMoney(order.total, order.currency)} cash`
                : `Driver remitted ${fmtMoney(order.total, order.currency)} to cashier`}
            </p>
            {driverHoldingCash && (
              <p className="mt-1 text-xs text-amber-800">
                Collected from customer {order.driver_cash_collected_at ? fmtTimeAgo(order.driver_cash_collected_at) : 'just now'}. The driver must hand this cash to a cashier on return — confirm below to send the driver an SMS receipt.
              </p>
            )}
            {driverRemitted && (
              <p className="mt-1 text-xs text-emerald-800">
                Received {order.driver_cash_remitted_at ? fmtTimeAgo(order.driver_cash_remitted_at) : 'recently'} · Receipt&nbsp;
                <span className="rounded bg-background px-1.5 py-0.5 font-mono">{order.driver_cash_remit_receipt}</span>
                {' '}· SMS confirmation sent to driver.
              </p>
            )}
          </div>
        )}

        {/* Items */}
        <div className="rounded-lg border border-border">
          <div className="border-b border-border bg-secondary/40 px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            Items ({order.items?.length || 0})
          </div>
          <ScrollArea className="max-h-[30vh]">
            <ul className="divide-y divide-border">
              {(order.items || []).map((it, idx) => (
                <li key={idx} data-testid="order-detail-item" className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium leading-tight">{it.quantity}× {it.name}</p>
                    {it.special_instructions && (
                      <p className="mt-0.5 text-xs italic text-muted-foreground">
                        “{it.special_instructions}”
                      </p>
                    )}
                  </div>
                  <span className="tabular">{fmtMoney(it.line_total, order.currency)}</span>
                </li>
              ))}
            </ul>
          </ScrollArea>
        </div>

        {order.special_instructions && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            <strong>Order note:</strong> {order.special_instructions}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {driverHoldingCash && onDriverRemit && (
            <Button
              data-testid="detail-driver-cash-remit"
              disabled={busy}
              onClick={() => onDriverRemit(order)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <HandCoins className="mr-1 h-4 w-4" /> Confirm cash received from driver
            </Button>
          )}
          {!isPaid && (order.payment_method === 'cash' || !order.payment_method) && (
            <Button data-testid="detail-mark-cash-paid" disabled={busy} onClick={() => onMarkCashPaid?.(order)}>
              <Wallet className="mr-1 h-4 w-4" /> Mark cash paid
            </Button>
          )}
          {!isPaid && onMarkMobileMoneyPaid && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="detail-mark-mobile-money-trigger"
                  variant="secondary"
                  disabled={busy}
                >
                  <Smartphone className="mr-1 h-4 w-4" /> Mark mobile money paid
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  data-testid="detail-mark-mtn-money-paid"
                  onClick={() => onMarkMobileMoneyPaid?.(order, 'mtn_money')}
                >
                  <Smartphone className="mr-2 h-4 w-4 text-amber-600" /> MTN Money
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="detail-mark-orange-money-paid"
                  onClick={() => onMarkMobileMoneyPaid?.(order, 'orange_money')}
                >
                  <Smartphone className="mr-2 h-4 w-4 text-orange-600" /> Orange Money
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isPaid && (
            <Button data-testid="detail-mark-card-paid" variant="secondary" disabled={busy} onClick={() => onMarkCardPaid?.(order)}>
              <CreditCard className="mr-1 h-4 w-4" /> Mark card paid
            </Button>
          )}
          <Button data-testid="detail-print-receipt" variant="ghost" onClick={() => onPrintReceipt?.(order)}>
            <Printer className="mr-1 h-4 w-4" /> Print receipt
          </Button>
          {canNoShow && onMarkNoShow && (
            <Button
              data-testid="detail-mark-no-show"
              variant="ghost"
              className="text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              disabled={busy}
              onClick={() => onMarkNoShow(order)}
              title={order.order_type === 'pickup' ? 'Customer never came to pick up' : 'Customer not reachable at delivery'}
            >
              <UserX className="mr-1 h-4 w-4" /> Mark no-show
            </Button>
          )}
          {canCancel && (
            <Button data-testid="detail-cancel-order" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" disabled={busy} onClick={() => onCancel?.(order)}>
              <Ban className="mr-1 h-4 w-4" /> Cancel
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            <X className="mr-1 h-4 w-4" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
